import { useState } from 'react';
import { Alert, Button, Card, Icon, Table, type Column } from '@evogard/ui';
import type { ManagementExits } from '@evogard/contract';
import { claimPhaseLabel, count, type ClaimFlowPeriod } from '@evogard/domain';
import { LINE_LABEL } from './managementLabels.js';
import { useManagementExits } from './useManagement.js';

/**
 * As negativas e as desistências (26/10/2026): dos sinistros abertos no
 * período, quem saiu do processo sem concluir, e de que etapa saiu.
 *
 * Duas listas lado a lado, cada uma com o total e a parte da coorte, e a
 * etapa de onde o sinistro veio — a `from_phase` da passagem do desfecho. A
 * passagem antiga que não registrou de onde veio aparece como tal, em vez de
 * ser atribuída a uma etapa qualquer.
 */

type Exit = ManagementExits['exits'][number];
const KINDS = [
  { kind: 'negado', title: 'Negativas' },
  { kind: 'desistencia', title: 'Desistências' },
] as const;

const share = (n: number, total: number) => (total ? Math.round((n / total) * 100) : 0);
const stageOf = (e: Exit) =>
  `${LINE_LABEL[e.department]} › ${
    e.phase === null ? 'etapa não registrada' : claimPhaseLabel(e.department, e.phase)
  }`;

export function ClaimExitsChart({ days }: { days: ClaimFlowPeriod }) {
  const [asTable, setAsTable] = useState(false);
  const query = useManagementExits(days);
  const data = query.data;
  const any = !!data?.exits.length;

  return (
    <Card
      as="figure"
      className="@container"
      title="Negativas e desistências"
      descricao="Quantos terminaram negados ou desistidos, e em que etapa estavam."
      actions={
        any &&
        !query.isError && (
          <Button variant="discreto" size="sm" onClick={() => setAsTable((v) => !v)}>
            <Icon nome={asTable ? 'grafico' : 'grade'} size={16} />
            {asTable ? 'Ver gráfico' : 'Ver tabela'}
          </Button>
        )
      }
    >
      {query.isError ? (
        <Alert tone="erro" title="Não conseguimos carregar as negativas e desistências">
          Nada foi desenhado, para não mostrar número velho como se fosse atual.
          <span className="mt-1 block font-mono text-xs">{query.error.message}</span>
        </Alert>
      ) : !data ? (
        <div
          className="h-40 animate-pulse rounded-borda bg-superficie-suave"
          aria-busy="true"
        />
      ) : !any ? (
        <p className="py-10 text-center text-sm text-texto-suave">
          Nenhum sinistro aberto nos últimos {days} dias foi negado ou desistido.
        </p>
      ) : asTable ? (
        <ExitsTable data={data} />
      ) : (
        <div className="grid gap-5 @2xl:grid-cols-2">
          {KINDS.map(({ kind, title }) => (
            <ExitList
              key={kind}
              title={title}
              exits={data.exits.filter((e) => e.kind === kind)}
              cohort={data.total}
            />
          ))}
        </div>
      )}
    </Card>
  );
}

function ExitList({
  title,
  exits,
  cohort,
}: {
  title: string;
  exits: Exit[];
  cohort: number;
}) {
  const total = exits.reduce((n, e) => n + e.total, 0);
  const max = Math.max(1, ...exits.map((e) => e.total));
  return (
    <section className="flex flex-col gap-3">
      <h3 className="flex items-baseline gap-2">
        <span className="text-sm font-semibold text-texto">{title}</span>
        <span className="text-xl font-bold tabular-nums text-texto">{total}</span>
        <span className="text-xs text-texto-suave">
          {share(total, cohort)}% dos {cohort} abertos
        </span>
      </h3>
      {exits.length === 0 ? (
        <p className="text-sm text-texto-suave">Nenhuma no período.</p>
      ) : (
        <ol className="flex flex-col gap-2" aria-label={`${title} por etapa`}>
          {exits.map((e) => (
            <li
              key={`${e.department}:${e.phase ?? ''}`}
              className="grid grid-cols-[minmax(0,1fr)_minmax(0,6rem)_auto] items-center gap-3 text-sm"
            >
              <span className="truncate text-texto-suave">{stageOf(e)}</span>
              <span className="h-2 overflow-hidden rounded-pilula bg-superficie-suave">
                <span
                  className="block h-full rounded-pilula bg-erro"
                  style={{ width: `${(e.total / max) * 100}%` }}
                />
              </span>
              <b className="w-8 text-right font-semibold tabular-nums text-texto">
                {e.total}
              </b>
            </li>
          ))}
        </ol>
      )}
      {total > 0 && (
        <p className="text-xs text-texto-suave">
          {count(total, 'sinistro saiu', 'sinistros saíram')} por aqui.
        </p>
      )}
    </section>
  );
}

function ExitsTable({ data }: { data: ManagementExits }) {
  const columns: Column<Exit>[] = [
    {
      title: 'Desfecho',
      cell: (r) => (r.kind === 'negado' ? 'Negativa' : 'Desistência'),
      alignment: 'inicio',
    },
    { title: 'Etapa de onde saiu', cell: stageOf, alignment: 'inicio' },
    { title: 'Sinistros', cell: (r) => r.total, alignment: 'fim' },
    {
      title: '% dos abertos',
      cell: (r) => `${share(r.total, data.total)}%`,
      alignment: 'fim',
    },
  ];
  return (
    <Table
      columns={columns}
      items={data.exits}
      rowKey={(r) => `${r.kind}:${r.department}:${r.phase ?? ''}`}
      label={`Negativas e desistências nos últimos ${data.days} dias`}
      empty="Nenhuma negativa nem desistência no período."
    />
  );
}
